import { SearchInputStore } from 'ui/standard/input/search_input/search_input_store';
import { action, computed, observable } from 'mobx';
import {
  EVERYTHING_POLICY,
  PolicyEntry,
  createPolicyEntries,
  createPolicyEntryOptions,
  getPolicyEntryID,
  removeEverythingPolicy,
} from 'ui/schedules/policy_entry/policy_entry_logic';
import { Schedule, SchedulePolicyMap } from 'services/pangolin/types';
import { notUndefined } from 'ui/base/types';
import { CustomInputStore } from './custom_input_modal/custom_input_store';


function validateCustomDomain(custom: string): boolean {
  /* eslint-disable-next-line no-useless-escape */
  const domain = /^((?:(?:(?:\w[\.\-\+]?)*)\w)+)((?:(?:(?:\w[\.\-\+]?){0,62})\w)+)\.(\w{2,6})$/;
  const stripped = custom.trim();
  return domain.test(stripped);
}

function validateCustomIP(custom: string): boolean {
  const ipv4 = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
  const ipv6 = /^((([0-9A-Fa-f]{1,4}:){7}([0-9A-Fa-f]{1,4}|:))|(([0-9A-Fa-f]{1,4}:){6}(:[0-9A-Fa-f]{1,4}|((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3})|:))|(([0-9A-Fa-f]{1,4}:){5}(((:[0-9A-Fa-f]{1,4}){1,2})|:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3})|:))|(([0-9A-Fa-f]{1,4}:){4}(((:[0-9A-Fa-f]{1,4}){1,3})|((:[0-9A-Fa-f]{1,4})?:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:))|(([0-9A-Fa-f]{1,4}:){3}(((:[0-9A-Fa-f]{1,4}){1,4})|((:[0-9A-Fa-f]{1,4}){0,2}:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:))|(([0-9A-Fa-f]{1,4}:){2}(((:[0-9A-Fa-f]{1,4}){1,5})|((:[0-9A-Fa-f]{1,4}){0,3}:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:))|(([0-9A-Fa-f]{1,4}:){1}(((:[0-9A-Fa-f]{1,4}){1,6})|((:[0-9A-Fa-f]{1,4}){0,4}:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:))|(:(((:[0-9A-Fa-f]{1,4}){1,7})|((:[0-9A-Fa-f]{1,4}){0,5}:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:)))(%.+)?$/;
  const stripped = custom.trim();
  return ipv4.test(stripped) || ipv6.test(stripped);
}

export class PolicyInputStore {
  search: SearchInputStore;
  customIP: CustomInputStore;
  customDomain: CustomInputStore;

  @observable.shallow entries: PolicyEntry[];
  @observable.shallow options: readonly PolicyEntry[];

  private readonly schedule: Schedule;

  constructor(schedule: Schedule) {
    this.schedule = schedule;
    this.search = new SearchInputStore();
    this.customIP = new CustomInputStore(validateCustomIP);
    this.customDomain = new CustomInputStore(validateCustomDomain);

    const { entries } = createPolicyEntries(this.schedule);
    this.entries = entries;
    this.options = createPolicyEntryOptions(entries);
  }

  @action.bound
  commitEntry(entry: PolicyEntry): void {
    this.entries.push(entry);
  }

  @action.bound
  removeEntry(entry: PolicyEntry): void {
    const id = getPolicyEntryID(entry);
    const index = this.entries.findIndex(e => getPolicyEntryID(e) === id);
    if (index >= 0) {
      this.entries.splice(index, 1);
    }
  }

  @action.bound
  commitIP() {
    const entry = this.customIP.commit();
    if (entry == null) {
      return;
    }
    this.entries.push(entry);
    this.customIP.hideModal();
    this.search.modal.hideModal();
  }

  @action.bound
  commitDomain() {
    // lower-case all custom websites (domains)
    this.customDomain.custom.value = this.customDomain.custom.value.toLowerCase();
    const entry = this.customDomain.commit();
    if (entry == null) {
      return;
    }
    this.entries.push(entry);
    this.customDomain.hideModal();
    this.search.modal.hideModal();
  }

  @computed
  get schedulePolicies(): SchedulePolicyMap {
    const { preserved } = createPolicyEntries(this.schedule);
    const entries = this.entries.map(removeEverythingPolicy).filter(notUndefined);
    return new SchedulePolicyMap([...preserved, ...entries].map(p => [p.uuid, p]));
  }

  @computed
  get hasEverythingPolicy(): boolean {
    return this.entries.some((entry) => entry.kind === EVERYTHING_POLICY);
  }
}
